import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useNavigate, Link } from "react-router-dom";
import { AiOutlineArrowLeft } from "react-icons/ai";
import Logo from "../assets/logo.jpg";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import apiClient from "../utils/apiClient";
import {
  loginroute,
  googleRegisterRoute,
} from "../utils/APIRoutes";
import { GoogleLogin } from "@react-oauth/google";

export default function Login() {
  const navigate = useNavigate();

  const [values, setValues] = useState({
    username: "",
    password: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Prevent duplicate authentication requests, including rapid Google callbacks.
  const authLock = useRef(false);
  const mountedRef = useRef(true);

  const toastOptions = {
    position: "bottom-right",
    autoClose: 8000,
    pauseOnHover: true,
    draggable: true,
    theme: "dark",
  };

  const getStorageKey = useCallback(() => {
    return process.env.REACT_APP_LOCALHOST_KEY;
  }, []);

  // Save the user object only after the backend confirms authentication.
  const saveAuthenticatedUser = useCallback((user) => {
    const key = getStorageKey();

    if (!key) {
      throw new Error(
        "Missing REACT_APP_LOCALHOST_KEY configuration."
      );
    }

    if (!user || typeof user !== "object" || !user._id) {
      throw new Error(
        "The server did not return a valid authenticated user."
      );
    }

    // If storage fails, throw instead of pretending login is complete.
    localStorage.setItem(key, JSON.stringify(user));

    // Verify the stored data can be read back.
    const savedUser = localStorage.getItem(key);

    if (!savedUser || JSON.parse(savedUser)._id !== user._id) {
      throw new Error("Could not verify the saved user.");
    }
  }, [getStorageKey]);

  // Convenience redirect for an existing locally saved user.
  useEffect(() => {
    mountedRef.current = true;

    try {
      const key = getStorageKey();

      if (!key) {
        console.error(
          "REACT_APP_LOCALHOST_KEY is not configured."
        );
        return;
      }

      const savedUser = localStorage.getItem(key);

      if (savedUser) {
        const user = JSON.parse(savedUser);

        if (user && typeof user === "object" && user._id) {
          navigate("/", { replace: true });
        }
      }
    } catch (error) {
      console.error("Could not read saved login:", error);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [getStorageKey, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const { username, password } = values;

    if (!username.trim() || !password) {
      toast.error(
        "Email and Password are required.",
        toastOptions
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm() || authLock.current) return;

    authLock.current = true;
    setIsSubmitting(true);

    try {
      const { data } = await apiClient.post(loginroute, {
        username: values.username.trim(),
        password: values.password,
      });

      if (data?.status !== true) {
        toast.error(
          data?.msg || "Login failed. Please try again.",
          toastOptions
        );
        return;
      }

      saveAuthenticatedUser(data.user);

      // Navigate only after the user data has been saved.
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Login error:", error);

      toast.error(
        error.response?.data?.msg ||
          error.message ||
          "Something went wrong. Please try again.",
        toastOptions
      );
    } finally {
      authLock.current = false;

      if (mountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleGoogleLogin = async (response) => {
    if (authLock.current) return;

    if (!response?.credential) {
      toast.error(
        "Google authentication did not return a credential. Please try again.",
        toastOptions
      );
      return;
    }

    authLock.current = true;
    setIsGoogleLoading(true);

    try {
      // The backend must verify this credential and establish
      // the BridgenApp HTTP-only session cookie.
      const { data } = await apiClient.post(
        googleRegisterRoute,
        { credential: response.credential }
      );

      if (data?.status !== true) {
        toast.error(
          data?.msg || "Google login failed.",
          toastOptions
        );
        return;
      }

      if (!data.user || !data.user._id) {
        throw new Error(
          "Google login succeeded without a valid user object."
        );
      }

      const userToSave = {
        ...data.user,
        isGoogleUser: true,
      };

      saveAuthenticatedUser(userToSave);

      // Replace the login history entry to prevent returning
      // to the login form when the Back button is pressed.
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Google login error:", error);

      toast.error(
        error.response?.data?.msg ||
          error.message ||
          "Google login failed. Please try again.",
        toastOptions
      );
    } finally {
      authLock.current = false;

      if (mountedRef.current) {
        setIsGoogleLoading(false);
      }
    }
  };

  const handleGoogleError = () => {
    toast.error(
      "Google login was cancelled or failed. Please try again.",
      toastOptions
    );
  };

  return (
    <>
      <Link to="/" aria-label="Go back to home">
        <AiOutlineArrowLeft
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            fontSize: 24,
            cursor: "pointer",
          }}
        />
      </Link>

      <FormContainer>
        <form onSubmit={handleSubmit}>
          <div className="brand">
            <img src={Logo} alt="BridgenApp logo" />

            <div>
              <h1>BridgenApp</h1>
              <p className="tagline">
                Shop, Chat and Gift!
              </p>
            </div>
          </div>

          <div className="google-login">
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={handleGoogleError}
              useOneTap={false}
            />

            {isGoogleLoading && (
              <p role="status" aria-live="polite">
                Completing Google sign-in...
              </p>
            )}
          </div>

          {/* Retained for compatibility with the existing form. */}
          <input
            type="text"
            placeholder="Username"
            name="username"
            autoComplete="username"
            value={values.username}
            onChange={handleChange}
            hidden
          />

          <input
            type="password"
            placeholder="Password"
            name="password"
            autoComplete="current-password"
            value={values.password}
            onChange={handleChange}
            hidden
          />

          <button
            type="submit"
            disabled={isSubmitting || isGoogleLoading}
            hidden
          >
            {isSubmitting ? "Logging in..." : "Log In"}
          </button>

          <p className="reset-password">
            Forgot password?{" "}
            <Link to="/request">Reset Password</Link>
          </p>

          <Link
            to="/privacy-policy"
            className="privacy-policy"
          >
            Privacy Policy
          </Link>
        </form>
      </FormContainer>

      <ToastContainer />
    </>
  );
}

const FormContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  background-color: #f0f0f0;

  .brand {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    width: 100%;

    img {
      width: 5rem;
      height: 5rem;
      object-fit: contain;
    }

    h1 {
      color: black;
      margin: 0;
    }

    .tagline {
      margin: 0;
      color: #996515;
    }
  }

  form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    width: 100%;
    max-width: 30rem;
    box-sizing: border-box;
    background-color: white;
    border-radius: 2rem;
    padding: clamp(1.25rem, 6vw, 3rem);
  }

  .google-login {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;

    p {
      text-align: center;
      color: #444;
      overflow-wrap: anywhere;
    }
  }

  input {
    box-sizing: border-box;
    background-color: white;
    padding: 1rem;
    border: 0.1rem solid #4e0eff;
    border-radius: 0.4rem;
    color: black;
    width: 100%;
    font-size: 1rem;

    &:focus {
      border-color: #997af0;
      outline: none;
    }
  }

  button {
    background-color: #4e0eff;
    color: white;
    width: 90%;
    padding: 1rem 2rem;
    border: none;
    font-weight: bold;
    cursor: pointer;
    border-radius: 0.4rem;
    font-size: 1rem;
    text-transform: uppercase;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .reset-password,
  .privacy-policy {
    margin: 0;
    text-align: center;
  }

  a {
    color: #4e0eff;
    text-decoration: none;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;

    form {
      border-radius: 1.25rem;
      gap: 1.5rem;
    }

    .brand {
      gap: 0.6rem;

      img {
        width: 3.5rem;
        height: 3.5rem;
      }

      h1 {
        font-size: 1.5rem;
      }
    }
  }
`;
